import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function NewsMarket() {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('portada');

  const filters = [
    { key: 'portada', label: 'Portada' },
    { key: 'regulacion-ue', label: 'Regulación UE' },
    { key: 'regulacion-es', label: 'Regulación España' },
    { key: 'eficiencia', label: 'Eficiencia/Ahorro' },
    { key: 'precios', label: 'Precios y mercados' },
    { key: 'ayudas', label: 'Ayudas/Subvenciones' },
    { key: 'tendencias', label: 'Tendencias' }
  ];

  // Datos actualizados - 3 Diciembre 2025
  // Fuentes: OMIE, MIBGAS, SENDECO2/Investing
  const indicators = [
    {
      title: 'Precio electricidad (España)',
      value: '€0.1522/kWh',
      change: '-27%',
      trend: 'down',
      link: 'https://www.omie.es/es/spot-hoy',
      source: 'OMIE - Mercado eléctrico'
    },
    {
      title: 'Precio gas natural (MIBGAS)',
      value: '€28.91/MWh',
      change: '-3.2%',
      trend: 'down',
      link: 'https://www.mibgas.es',
      source: 'MIBGAS - Mercado ibérico'
    },
    {
      title: 'Precio del carbono (EU ETS)',
      value: '€81.79/tCO₂',
      change: '+2.1%',
      trend: 'up',
      link: 'https://www.sendeco2.com/es/precios-co2',
      source: 'SENDECO2'
    }
  ];

  const news = [
    // Noticias destacadas para portada (featured: true)
    {
      title: 'Nuevas subvenciones para biomasa industrial: hasta 5M€ por proyecto',
      source: 'IDAE',
      date: '1 Dic 2024',
      category: 'Ayudas/Subvenciones',
      categoryKey: 'ayudas',
      summary: 'El IDAE lanza una línea de ayudas directas para instalaciones de biomasa en industrias, cubriendo hasta el 40% de la inversión',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=400&h=250&fit=crop',
      link: 'https://www.idae.es',
      featured: true,
      priority: 1
    },
    {
      title: 'La UE endurece obligaciones de descarbonización para industrias',
      source: 'Comisión Europea',
      date: '28 Nov 2024',
      category: 'Regulación UE',
      categoryKey: 'regulacion-ue',
      summary: 'Nueva directiva obliga a industrias de alto consumo a reducir emisiones un 55% para 2030. La biomasa se posiciona como solución clave',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=250&fit=crop',
      link: 'https://ec.europa.eu/commission/presscorner/detail/es/ip_24_1965',
      featured: true,
      priority: 2
    },
    {
      title: 'Incentivos fiscales para instalaciones de biomasa térmica en industria',
      source: 'BOE',
      date: '25 Nov 2024',
      category: 'Regulación España',
      categoryKey: 'regulacion-es',
      summary: 'Real Decreto establece deducciones fiscales del 30% para inversiones en sistemas de biomasa que sustituyan combustibles fósiles',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=250&fit=crop',
      link: 'https://www.boe.es',
      featured: true,
      priority: 3
    },
    // Otras noticias relevantes
    {
      title: 'Ayudas Next Generation para calderas de biomasa industriales',
      source: 'MITECO',
      date: '20 Nov 2024',
      category: 'Ayudas/Subvenciones',
      categoryKey: 'ayudas',
      summary: 'Ampliación del programa de fondos europeos para sustitución de calderas fósiles por biomasa en el sector industrial',
      image: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=400&h=250&fit=crop',
      link: 'https://www.miteco.gob.es/es/prensa/ultimas-noticias.html',
      featured: false
    },
    {
      title: 'Nueva directiva europea sobre eficiencia energética en edificios',
      source: 'Comisión Europea',
      date: '15 Nov 2024',
      category: 'Regulación UE',
      categoryKey: 'regulacion-ue',
      summary: 'La UE establece nuevos objetivos de reducción del consumo energético del 11.7% para 2030',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=250&fit=crop',
      link: 'https://ec.europa.eu/commission/presscorner/detail/es/ip_24_1965',
      featured: false
    },
    {
      title: 'España aprueba Plan de Impulso a la Biomasa Térmica 2024-2030',
      source: 'MITECO',
      date: '14 Nov 2024',
      category: 'Regulación España',
      categoryKey: 'regulacion-es',
      summary: 'El plan contempla medidas para multiplicar por 3 la potencia instalada de biomasa en industrias españolas',
      image: 'https://images.unsplash.com/photo-1473186578172-c141e6798cf4?w=400&h=250&fit=crop',
      link: 'https://www.miteco.gob.es',
      featured: false
    },
    {
      title: 'Precios de la energía: tendencias para el primer trimestre 2025',
      source: 'OMIE',
      date: '10 Nov 2024',
      category: 'Precios y mercados',
      categoryKey: 'precios',
      summary: 'Análisis de las proyecciones de precios energéticos para los próximos meses',
      image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=250&fit=crop',
      link: 'https://www.omie.es/es/market-results',
      featured: false
    },
    {
      title: 'Certificaciones de biomasa sostenible: nuevos requisitos UE',
      source: 'Comisión Europea',
      date: '8 Nov 2024',
      category: 'Regulación UE',
      categoryKey: 'regulacion-ue',
      summary: 'Actualización de los criterios de sostenibilidad para biomasa industrial en el marco de la directiva RED III',
      image: 'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=400&h=250&fit=crop',
      link: 'https://ec.europa.eu',
      featured: false
    },
    {
      title: 'Convocatoria extraordinaria: ayudas para auditorías energéticas',
      source: 'IDAE',
      date: '5 Nov 2024',
      category: 'Ayudas/Subvenciones',
      categoryKey: 'ayudas',
      summary: 'Subvención del 70% para auditorías que evalúen viabilidad de sistemas de biomasa en industrias',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&h=250&fit=crop',
      link: 'https://www.idae.es',
      featured: false
    }
  ];

  const filteredNews = activeFilter === 'portada'
    ? news.filter(article => article.featured).sort((a, b) => (a.priority || 999) - (b.priority || 999))
    : news.filter(article => article.categoryKey === activeFilter);

  return (
    <section id="noticias" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            {t('news.titulo')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Regulación, ayudas y precios que impactan tus decisiones energéticas. Selección automática de fuentes oficiales y medios de referencia.
          </p>
        </div>

        {/* Indicadores rápidos */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {indicators.map((indicator, index) => (
            <div
              key={index}
              className="bg-gray-50 rounded-xl p-6 text-center hover:bg-gray-100 transition-colors duration-200"
            >
              <h3 className="font-semibold text-gray-900 mb-3 text-sm">
                {indicator.title}
              </h3>
              <div className="text-2xl font-bold text-gray-900 mb-2">
                {indicator.value}
              </div>
              <div className={`flex items-center justify-center space-x-1 text-sm ${
                indicator.trend === 'up' ? 'text-red-600' : 'text-green-600'
              }`}>
                <i className={`ri-arrow-${indicator.trend === 'up' ? 'up' : 'down'}-line`}></i>
                <span>{indicator.change}</span>
              </div>
              <a 
                href={indicator.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-600 hover:text-teal-700 text-sm mt-2 cursor-pointer inline-block"
              >
                ver indicador
              </a>
            </div>
          ))}
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {filters.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                activeFilter === filter.key
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Noticias */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {filteredNews.map((article, index) => (
            <article
              key={index}
              className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100"
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={article.image} 
                  alt={article.title}
                  className="w-full h-full object-cover object-center"
                />
              </div>
              
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-teal-600 bg-teal-100 px-2 py-1 rounded">
                    {article.category}
                  </span>
                  <span className="text-xs text-gray-500">{article.date}</span>
                </div>
                
                <h3 className="font-semibold text-gray-900 mb-3 leading-tight">
                  {article.title}
                </h3>
                
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  {article.summary}
                </p>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">{article.source}</span>
                  <a 
                    href={article.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:text-teal-700 text-sm cursor-pointer inline-flex items-center gap-1"
                  >
                    {t('news.leerMas')} <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="text-center">
          <p className="text-sm text-gray-500 italic">
            Contenido curado de fuentes verificadas; enlazamos siempre a la publicación original.
          </p>
        </div>
      </div>
    </section>
  );
}
